import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { formatMoney } from "@/lib/format";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { ArrowUpRight, Bell, Check, ExternalLink, ShieldAlert } from "lucide-react";

const CHART_PALETTE = ["#0F766E", "#1D4ED8", "#C46A3A", "#7C3AED", "#B42318", "#1F7A4D", "#B45309", "#0891B2"];

function Kpi({ label, value, sublabel, testId, href }) {
    return (
        <Card className="p-4 border-border" data-testid={testId}>
            <div className="flex items-start justify-between gap-3"><div><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</div>{sublabel && <div className="text-xs text-muted-foreground mt-1">{sublabel}</div>}</div>{href && <Link to={href} className="text-muted-foreground hover:text-primary" aria-label={`View ${label.toLowerCase()}`}><ArrowUpRight className="h-4 w-4" /></Link>}</div>
        </Card>
    );
}

function ViewFull({ to }) {
    return <Link to={to} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">View full <ArrowUpRight className="h-3.5 w-3.5" /></Link>;
}

function AnalyticsAlerts({ recommendationsReady }) {
    const qc = useQueryClient();
    const { data: notifications = [] } = useQuery({
        queryKey: ["notifications"],
        queryFn: () => apiGet("/notifications"),
        enabled: recommendationsReady !== false,
    });
    const activeKeys = new Set((recommendationsReady || []).map((item) => item.alert_key).filter(Boolean));
    const alerts = notifications
        .filter((item) => item.kind === "analytics_recommendation" && activeKeys.has(item.alert_key))
        .sort((a, b) => Number(!a.read) - Number(!b.read) || Number(b.severity === "warning") - Number(a.severity === "warning") || String(b.created_at).localeCompare(String(a.created_at)));
    const unread = alerts.filter((item) => !item.read).length;
    const markRead = async (notification) => {
        if (notification.read) return;
        await apiPost(`/notifications/${notification.id}/read`);
        qc.invalidateQueries({ queryKey: ["notifications"] });
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary/40 hover:text-primary" aria-label={`Analytics alerts${unread ? `, ${unread} unread` : ""}`} data-testid="analytics-alerts-trigger">
                    <Bell className="h-4 w-4" strokeWidth={1.8} />
                    {unread > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-5 text-primary-foreground">{unread > 9 ? "9+" : unread}</span>}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(380px,calc(100vw-2rem))] p-0">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div><p className="font-display text-base font-semibold">Analytics alerts</p><p className="text-xs text-muted-foreground">Live recommendations for this workspace</p></div>
                    <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{alerts.length} active</span>
                </div>
                <div className="max-h-[min(420px,60vh)] overflow-y-auto p-2">
                    {alerts.length === 0 && <div className="px-3 py-8 text-center"><Check className="mx-auto h-6 w-6 text-primary" /><p className="mt-2 text-sm font-medium">No active alerts</p><p className="mt-1 text-xs text-muted-foreground">Your current analytics look healthy.</p></div>}
                    {alerts.map((alert) => <button type="button" key={alert.id} onClick={() => markRead(alert)} className={`group w-full rounded-lg border p-3 text-left transition hover:border-primary/40 ${alert.read ? "border-border bg-background" : alert.severity === "warning" ? "border-amber-200 bg-amber-50/80" : "border-border bg-muted/30"}`}>
                        <div className="flex items-start gap-3"><span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${alert.severity === "warning" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>{alert.severity === "warning" ? <ShieldAlert className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}</span><span className="min-w-0 flex-1"><span className="flex items-center gap-2"><span className="text-sm font-semibold">{alert.title}</span>{!alert.read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}</span><span className="mt-1 block text-xs leading-5 text-muted-foreground">{alert.body}</span></span></div>
                    </button>)}
                </div>
                <div className="border-t border-border px-4 py-3"><Link to="/analytics/recommendations" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Review all recommendations <ExternalLink className="h-3.5 w-3.5" /></Link></div>
            </PopoverContent>
        </Popover>
    );
}

export default function AnalyticsPage() {
    const { data: overview } = useQuery({ queryKey: ["an-overview"], queryFn: () => apiGet("/analytics/overview") });
    const { data: util = [] } = useQuery({ queryKey: ["an-util"], queryFn: () => apiGet("/analytics/utilization") });
    const { data: revenueBySvc = [] } = useQuery({ queryKey: ["an-rev-svc"], queryFn: () => apiGet("/analytics/revenue-by-service") });
    const { data: revenueByEng = [] } = useQuery({ queryKey: ["an-rev-eng"], queryFn: () => apiGet("/analytics/revenue-by-engineer") });
    const { data: heatmap } = useQuery({ queryKey: ["an-heat"], queryFn: () => apiGet("/analytics/occupancy-heatmap") });
    const recsQuery = useQuery({ queryKey: ["an-recs"], queryFn: () => apiGet("/analytics/recommendations") });
    const recs = recsQuery.data || [];

    const heatMax = heatmap ? Math.max(...heatmap.grid.flat()) : 1;

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <PageHeader title="Analytics" subtitle="Charts are nice. Recommendations are nicer." testId="analytics-header" actions={<AnalyticsAlerts recommendationsReady={recsQuery.isSuccess ? recs : false} />} />

            {overview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Kpi label="Total revenue" value={formatMoney(overview.total_revenue)} href="/invoices" testId="kpi-total-rev" />
                    <Kpi label="Outstanding" value={formatMoney(overview.outstanding)} href="/invoices" testId="kpi-outstanding" />
                    <Kpi label="Bookings" value={overview.total_bookings} href="/bookings" sublabel={`${overview.conversion_rate}% conversion`} testId="kpi-bookings" />
                    <Kpi label="Active clients" value={overview.active_clients} href="/crm" testId="kpi-clients" />
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
                <Card className="p-4 border-border" data-testid="chart-room-util">
                    <div className="mb-3 flex items-center justify-between gap-3"><h3 className="font-display text-base font-semibold">Room utilization</h3><ViewFull to="/analytics/rooms" /></div>
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer>
                            <BarChart data={util}>
                                <CartesianGrid stroke="#E9E3D9" strokeDasharray="3 3" />
                                <XAxis dataKey="room_name" tick={{ fill: "#7A7A7A", fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                                <YAxis tick={{ fill: "#7A7A7A", fontSize: 11 }} unit="%" />
                                <Tooltip contentStyle={{ background: "#111", color: "#fff", border: "none", borderRadius: 8 }} />
                                <Bar dataKey="utilization_pct" radius={[6, 6, 0, 0]} >
                                    {util.map((u) => <Cell key={u.room_id} fill={u.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-4 border-border" data-testid="chart-rev-service">
                    <div className="mb-3 flex items-center justify-between gap-3"><h3 className="font-display text-base font-semibold">Revenue by service</h3><ViewFull to="/analytics/services" /></div>
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer>
                            <BarChart data={revenueBySvc} layout="vertical">
                                <CartesianGrid stroke="#E9E3D9" strokeDasharray="3 3" />
                                <XAxis type="number" tick={{ fill: "#7A7A7A", fontSize: 11 }} />
                                <YAxis type="category" dataKey="service_name" tick={{ fill: "#7A7A7A", fontSize: 11 }} width={140} />
                                <Tooltip contentStyle={{ background: "#111", color: "#fff", border: "none", borderRadius: 8 }} />
                                <Bar dataKey="revenue" fill="#0F766E" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-4 border-border" data-testid="chart-rev-engineer">
                    <div className="mb-3 flex items-center justify-between gap-3"><h3 className="font-display text-base font-semibold">Revenue by engineer</h3><ViewFull to="/analytics/engineers" /></div>
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={revenueByEng} dataKey="revenue" nameKey="engineer_name" innerRadius={50} outerRadius={80}>
                                    {revenueByEng.map((entry, i) => <Cell key={entry.engineer_id} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#111", color: "#fff", border: "none", borderRadius: 8 }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-4 border-border" data-testid="chart-heatmap">
                    <div className="mb-3 flex items-center justify-between gap-3"><h3 className="font-display text-base font-semibold">Occupancy heatmap</h3><ViewFull to="/analytics/occupancy" /></div>
                    {heatmap && (
                        <div>
                            <div className="grid gap-0.5" style={{ gridTemplateColumns: `40px repeat(24, 1fr)` }}>
                                <div />
                                {heatmap.hours.map((h) => <div key={h} className="text-[9px] text-muted-foreground text-center">{h}</div>)}
                                {heatmap.days.flatMap((d, di) => [
                                    <div key={`day-${d}`} className="text-xs text-muted-foreground pr-1 self-center">{d}</div>,
                                    ...heatmap.grid[di].map((v, hi) => {
                                        const intensity = heatMax > 0 ? v / heatMax : 0;
                                        const bg = `rgba(15, 118, 110, ${intensity})`;
                                        return <div key={`c-${di}-${hi}`} title={`${d} ${hi}:00 — ${v.toFixed(1)}h`} className="h-6 rounded-sm border border-border" style={{ backgroundColor: intensity ? bg : "#F7F5F1" }} />;
                                    }),
                                ])}
                            </div>
                        </div>
                    )}
                </Card>
            </div>

        </div>
    );
}